import { foldForSearch } from "./search.js";
/**
 * The nature of a stored or travelling item. A server from before 0093 sends
 * neither flag, and both held then; one from before 0060 sends no kind, and
 * every item was a service.
 */
export function natureOf(item) {
    const countsStock = item.countsStock ?? true;
    return { kind: item.kind === "PRODUCT" ? "PRODUCT" : "SERVICE", forSale: item.forSale ?? true, countsStock };
}
/** Whether it is offered where things are sold: a ticket, a sale, the shop. */
export function sells(nature) {
    return nature.kind === "SERVICE" || nature.forSale;
}
/** Whether this business counts it now: what the forms ask the first count of. */
export function isCounted(nature, inventory) {
    return inventory && nature.kind === "PRODUCT" && nature.countsStock;
}
/**
 * WHETHER A PRICE OF IT IS COUNTED AFTER A SAVE -- `syncTracking`'s rule.
 * With inventory: a product that counts. Without, it never switches counting
 * ON (hiding the module is not stopping), and only switches OFF what stopped
 * being a counted product.
 */
export function wantsTracking(nature, inventory, tracked) {
    return nature.kind === "PRODUCT" && nature.countsStock && (inventory || tracked);
}
/**
 * What the form sends from what it shows: whatever does not sell is counted.
 * It holds for a service too -- the form may keep a product's «no se vende»
 * with counting off after the question turned to «Servicio» -- because 0093's
 * check knows no kind, and the pair would be a 400.
 */
export function natureToSave(asked) {
    return asked.forSale ? asked : { ...asked, countsStock: true };
}
/**
 * WHICH SWITCHES THE FORM DRAWS (§B.4): only for a product, only with
 * inventory. A menu's dish always sells, so it is asked only about counting;
 * and counting is not asked while it does not sell -- what is not sold is counted.
 * `keepsSaleSwitch` is an item saved as not selling: it keeps «Se vende» even
 * without inventory so it can be sold again, but counting still needs the
 * real inventory.
 */
export function itemSwitches(asked, where) {
    const product = asked.kind === "PRODUCT";
    return {
        sells: product && (where.inventory || where.keepsSaleSwitch === true) && where.onlyKind === null,
        counts: product && where.inventory && asked.forSale,
    };
}
/**
 * The two switches' words, the same on both clients. `on` and `off` are the
 * line always drawn under a switch, saying what its state means: the switch
 * alone does not explain itself.
 */
export const ITEM_SWITCHES = {
    forSale: { label: "Se vende", on: "Aparece al vender", off: "Lo usas en el negocio: no aparece al vender" },
    countsStock: { label: "Contar cuántos hay", on: "Lleva existencias: cada venta las descuenta", off: "No se anotan existencias" },
};
/** Whether saving `after` over `before` stops counting it: the save asks first (`STOPS_COUNTING_WARNING`). */
export function stopsCounting(before, after) {
    return before.variants.some((variant) => variant.tracksStock) && (after.kind === "SERVICE" || !after.countsStock);
}
/** What stopping stock counting costs one item or a whole section: one sentence for both. */
export const STOPS_COUNTING_WARNING = "Dejará(n) de contarse y ya no dirá(n) cuántos hay. Su historial se queda.";
/** The words of the question a save asks when `stopsCounting`: the same on both clients. */
export function stopsCountingConfirm(after) {
    const toService = after.kind === "SERVICE";
    return {
        title: toService ? "¿Cambiar a Servicio?" : "¿Dejar de contarlo?",
        message: STOPS_COUNTING_WARNING,
        confirmLabel: toService ? "Cambiar" : "Dejar de contar",
    };
}
const TAG_ORDER = ["SERVICE", "PRODUCT", "NOT_FOR_SALE"];
export const CATALOG_ITEM_TAGS = {
    SERVICE: { label: "Servicio", filter: "Servicios" },
    PRODUCT: { label: "Producto", filter: "Productos" },
    NOT_FOR_SALE: { label: "No se vende", filter: "No se venden" },
};
export function catalogItemTag(item) {
    const nature = natureOf(item);
    return nature.kind === "SERVICE" ? "SERVICE" : nature.forSale ? "PRODUCT" : "NOT_FOR_SALE";
}
/** The tags «Tipo» offers: «No se venden» only where inventory is kept, as the switch that makes one. */
export function catalogTagChoices(inventory) {
    return TAG_ORDER.filter((tag) => inventory || tag !== "NOT_FOR_SALE");
}
/** The list's filter for a tag: what `GET /catalog/items` reads as `kind` and `forSale`. */
export function catalogTagFilter(tag) {
    if (tag === "SERVICE")
        return { kind: "SERVICE" };
    return { kind: "PRODUCT", forSale: tag === "PRODUCT" };
}
/** A tag from an address or a select, or null for anything else. */
export function catalogTagOf(raw) {
    return TAG_ORDER.find((tag) => tag === raw) ?? null;
}
const NATURE_WORDS = [
    [{ kind: "PRODUCT", forSale: true }, ["material", "materiales", "producto", "productos", "repuesto", "repuestos", "articulo", "articulos", "mercancia"]],
    [{ kind: "SERVICE", forSale: true }, ["trabajo", "trabajos", "mano de obra", "servicio", "servicios", "labor"]],
    [{ kind: "PRODUCT", forSale: false }, ["insumo", "insumos", "consumible", "consumibles"]],
];
/**
 * The import's guess from a section's name: the words a real price list uses
 * for its groups, folded. Anything else is null -- the import decides (§B.6).
 */
export function natureFromSectionName(name) {
    const folded = foldForSearch(name.trim().replace(/\s+/g, " "));
    for (const [nature, words] of NATURE_WORDS)
        if (words.includes(folded))
            return nature;
    return null;
}
